<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { TagsInputItem, TagsInputItemDelete, TagsInputItemOptions, TagsInputItemText } from '../ui/tags-input'
import { useUpdateTagMutation } from '~~/src/composables/mutations/tags/useUpdateTagMutation'
import type { TagData } from '~~/src/api/tags'
import { useDeleteTagMutation } from '~~/src/composables/mutations/tags/useDeleteTagMutation'
import { useRemoveElementalTagMutation } from '~~/src/composables/mutations/tags/useRemoveElementalTagMutation'

const props = defineProps<Props>()
const emits = defineEmits<{
  'remove': [TagData]
  'edit': [id:string]
  'closeEdit': [id:string]
  'update': [tag:TagData]
  'selected': [tag:TagData]
}>()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')
interface Props {
  tag: TagData
  isEditing: boolean
  elementalId?: string | null

}
const inputRef = ref<HTMLInputElement>()
const { mutate: updateTag } = useUpdateTagMutation()
const { mutate: deleteTag } = useDeleteTagMutation()
const { mutate: removeElementalTag } = useRemoveElementalTagMutation()

const elementalId = toRef(props, 'elementalId')

const user = useUser()
const userId = computed(() => user.value?.id)

const toast = useToast()
const title = ref(props.tag.title)

const isLongPress = ref(false)
const isOptionsOpen = ref(false)
let longPressTimer: NodeJS.Timeout | null = null

const tagItemRef = ref<HTMLElement>()

const handleTouchStart = (e: TouchEvent) => {
  if (!isMobile.value)
    return
  e.preventDefault()
  longPressTimer = setTimeout(() => {
    isLongPress.value = true
    isOptionsOpen.value = true
  }, 250)
}

const handleTouchEnd = (_event: TouchEvent) => {
  if (!isMobile.value)
    return
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
  if (!isLongPress.value)
    emits('selected', props.tag)
}

const handleTouchCancel = () => {
  if (!isMobile.value)
    return
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
  isLongPress.value = false
  isOptionsOpen.value = false
}

function handlerCloseEdit() {
  title.value = props.tag.title
  emits('closeEdit', props.tag.id)
}
const handleDeleteTag = async (item: TagData) => {
  deleteTag({
    tagId: item.id,
    userId,
    elementalId,
  }, {
    onError() {
      toast.error('Error deleting tag')
    },
  })
  isLongPress.value = false
}
const handleRenameTag = async (item: TagData, name: string) => {
  const trimmedName = name.trim()
  const trimmedTitle = title.value.trim()
  if (trimmedName.length < 3 || !item.id || trimmedTitle.length < 3)
    return toast.error('Tag name must be at least 3 characters')
  updateTag({
    tagId: item.id,
    tag: {
      ...item,
      title: trimmedTitle,
    },
    userId,
    elementalId,
  }, {
    onSuccess() {
      emits('closeEdit', props.tag.id)
    },
    onError(err) {
      toast.error(err?.message || 'Error renaming tag')
      emits('closeEdit', props.tag.id)
    },
  })
}

const handleRenameTagStart = (id: string) => {
  emits('edit', id)
  setTimeout(() => {
    if (inputRef.value)
      inputRef.value?.focus()
    isLongPress.value = false
  }, 200)
}

const handleColorChange = async (item: TagData, color: string) => {
  if (!item.id)
    return
  updateTag({
    tagId: item.id,
    tag: {
      ...item,
      color,
    },
    elementalId,
    userId,
  }, {
    onError(err) {
      toast.error(err?.message || 'Error changing tag color')
    },
  })
  isLongPress.value = false
}
const removeFromTagList = async (item: TagData) => {
  if (!elementalId.value)
    return
  const elementalIdRaw = elementalId.value
  removeElementalTag({
    elementalId: elementalIdRaw,
    tagId: item.id,
  })
  isLongPress.value = false
}
</script>

<template>
  <TagsInputItem
    ref="tagItemRef"
    :value="tag.title"
    :style="{ backgroundColor: tag.color }"
    class="group relative tag-menu-item justify-center"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
    @touchcancel="handleTouchCancel"
    @click="!isMobile && $emit('selected', tag)"
  >
    <TagsInputItemText v-if="!isEditing" class="break-words truncate" />
    <input
      v-else
      ref="inputRef"
      v-model="title"
      type="text"
      maxlength="100"
      @keydown.enter="() => handleRenameTag(tag, tag.title)"
      @keydown.esc="handlerCloseEdit"
    >

    <div
      v-if="!isEditing && ((isMobile && isOptionsOpen) || !isMobile)"
      class="flex absolute right-0 bg-white rounded-md transition-all"
      :class="{
        'opacity-0 md:group-hover:opacity-100 md:group-active:opacity-100': !isMobile,
        'opacity-100': isMobile && isOptionsOpen,
        'opacity-0': isMobile && !isOptionsOpen,
      }"
    >
      <div class="flex rounded-md" :style="{ backgroundColor: tag.color }">
        <TagsInputItemOptions
          v-model="isOptionsOpen"
          :tag-id="tag.id"
          :color="tag.color"
          :elemental-id="elementalId"
          @rename="() => {
            handleRenameTagStart(tag.id)
          }"
          @delete="() => {
            handleDeleteTag(tag)
          }"
          @color-change="(color) => {
            handleColorChange(tag, color)
          }"
          @remove-from-elemental="() => {
            removeFromTagList(tag)
          }"
        />
        <TagsInputItemDelete v-if="elementalId && !isMobile" @click.stop="removeFromTagList(tag)" />
      </div>
    </div>
  </TagsInputItem>
</template>
